import { useQuery } from 'react-query';
import { IPaginationParams } from '../../../types';
import { IOccurrenceFilterParams } from '../../types';
import {
  getAllOccurrenceByLicenseAsync,
  getAllOccurrenceCountAsync,
  getAllOccurrenceSummaryAsync,
  getOccurrenceByIdAsync,
  checkCriticalAlertAsync,
  getStudentsForOccurrenceAsync,
  getResponsiblesForOccurrenceAsync,
  getGeneralsForOccurrenceAsync,
  getVehiclesForOccurrenceAsync,
  getBlockedTurnstileLogsAsync,
  getBlockedTurnstileLogsCountAsync,
} from '../../index';

export const OCCURRENCE_KEYS = {
  ALL: 'occurrence',
  LIST: 'list',
  COUNT: 'count',
  SUMMARY: 'summary',
  DETAIL: 'detail',
  CHECK: 'check',
  LOOKUP_STUDENTS: 'lookup-students',
  LOOKUP_RESPONSIBLES: 'lookup-responsibles',
  LOOKUP_GENERALS: 'lookup-generals',
  LOOKUP_VEHICLES: 'lookup-vehicles',
  BLOCKED_LOGS: 'blocked-logs',
  BLOCKED_LOGS_COUNT: 'blocked-logs-count',
};

const defaultQueryOptions = {
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 2,
};

const lookupQueryOptions = {
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 10,
};

export const useGetAllOccurrenceByLicenseQuery = (
  pagination?: IPaginationParams,
  filter?: IOccurrenceFilterParams
) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.LIST, pagination, filter],
    () => getAllOccurrenceByLicenseAsync(pagination, filter),
    {
      ...defaultQueryOptions,
      keepPreviousData: true,
    }
  );
};

export const useGetOccurrenceCountQuery = (
  filter?: IOccurrenceFilterParams
) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.COUNT, filter],
    () => getAllOccurrenceCountAsync(filter),
    {
      ...defaultQueryOptions,
      keepPreviousData: true,
    }
  );
};

export const useGetOccurrenceSummaryQuery = (searchTerm?: string) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.SUMMARY, searchTerm],
    () => getAllOccurrenceSummaryAsync(searchTerm),
    {
      ...defaultQueryOptions,
      keepPreviousData: true,
    }
  );
};

export const useGetOccurrenceByIdQuery = (id?: number | null) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.DETAIL, id],
    () => getOccurrenceByIdAsync(id!),
    {
      ...defaultQueryOptions,
      enabled: Boolean(id && id > 0),
    }
  );
};

export const useCheckCriticalAlertQuery = (recognitionId?: number | null) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.CHECK, recognitionId],
    () => checkCriticalAlertAsync(recognitionId!),
    {
      refetchOnWindowFocus: false,
      staleTime: 0,
      enabled: Boolean(recognitionId && recognitionId > 0),
    }
  );
};

export const useGetStudentsForOccurrenceQuery = (enabled = true) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.LOOKUP_STUDENTS],
    () => getStudentsForOccurrenceAsync(),
    {
      ...lookupQueryOptions,
      enabled,
    }
  );
};

export const useGetResponsiblesForOccurrenceQuery = (enabled = true) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.LOOKUP_RESPONSIBLES],
    () => getResponsiblesForOccurrenceAsync(),
    {
      ...lookupQueryOptions,
      enabled,
    }
  );
};

export const useGetGeneralsForOccurrenceQuery = (enabled = true) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.LOOKUP_GENERALS],
    () => getGeneralsForOccurrenceAsync(),
    {
      ...lookupQueryOptions,
      enabled,
    }
  );
};

export const useGetVehiclesForOccurrenceQuery = (enabled = true) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.LOOKUP_VEHICLES],
    () => getVehiclesForOccurrenceAsync(),
    {
      ...lookupQueryOptions,
      enabled,
    }
  );
};

export const useGetBlockedTurnstileLogsQuery = (
  pagination?: IPaginationParams,
  searchTerm?: string
) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.BLOCKED_LOGS, pagination, searchTerm],
    () => getBlockedTurnstileLogsAsync(pagination, searchTerm),
    {
      ...defaultQueryOptions,
      keepPreviousData: true,
    }
  );
};

export const useGetBlockedTurnstileLogsCountQuery = (searchTerm?: string) => {
  return useQuery(
    [OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.BLOCKED_LOGS_COUNT, searchTerm],
    () => getBlockedTurnstileLogsCountAsync(searchTerm),
    {
      ...defaultQueryOptions,
      keepPreviousData: true,
    }
  );
};